// The Shop lights part of the Doors screen (Admin): each light switch's relay, whether its lights are on, where the
// after-hours auto-off stands, On / Off, and the auto-off switch. Wording + calls: features/security/lights.js and
// useShopLights.js. It reads apart from the door board and shows nothing until a relay exists, so a failed read here
// never disturbs the rest of the page. Mirrors DoorLocksSection.jsx.

import React from 'react';
import { desktopUi } from '../features/desktopUi';
import { useLightControls, useShopLights } from '../features/security/useShopLights';
import {
  autoOffNote,
  lightActivity,
  lightButtonsDisabled,
  lightHealthLine,
  lightStateView,
  pendingText,
} from '../features/security/lights';

const TONE = {
  ok: { bg: '#d1f2dd', fg: '#14532d', border: '#16a34a' },
  bad: { bg: '#fde2e1', fg: '#7f1d1d', border: '#dc2626' },
  warn: { bg: '#fff1cc', fg: '#713f12', border: '#d97706' },
  muted: { bg: '#e9ecef', fg: '#495057', border: '#adb5bd' },
};

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 },
  card: (tone) => ({ borderLeft: `6px solid ${TONE[tone].border}` }),
  badge: (tone) => ({ background: TONE[tone].bg, color: TONE[tone].fg, border: `1px solid ${TONE[tone].border}`,
    borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }),
  small: { fontSize: 13 },
  activity: (tone) => ({ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, color: TONE[tone].fg }),
};

function LightCard({ relay, data, readFailed, onChanged, onRefused }) {
  const { busy, msg, press, toggleAuto } = useLightControls({ relay, ui: desktopUi, onChanged, onRefused });
  const view = lightStateView(relay, readFailed);
  const health = lightHealthLine(relay);
  const pending = pendingText(relay);
  const activity = lightActivity(data, relay.relay_id, 6);
  const off = busy || lightButtonsDisabled(relay);

  return (
    <div className="card" style={styles.card(view.tone)} data-testid={`light-${relay.relay_id}`}>
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h5 className="mb-0">{relay.name}</h5>
          <span style={styles.badge(view.tone)}>{view.label}</span>
        </div>
        {view.note && <div className="mt-1" style={{ ...styles.small, color: TONE[view.tone].fg }}>{view.note}</div>}
        {relay.state_text && <div className="mt-1" style={styles.small}>{relay.state_text}</div>}
        {health && <div className="mt-1 text-muted" style={styles.small}>{health}</div>}
        <div className="d-flex gap-2 mt-2">
          <button type="button" className="btn btn-sm btn-warning" disabled={off} onClick={() => press('on')}>On</button>
          <button type="button" className="btn btn-sm btn-outline-dark" disabled={off} onClick={() => press('off')}>Off</button>
        </div>
        {pending && <div className="mt-1 text-muted" style={styles.small}>{pending}</div>}
        {msg && <div className={`alert alert-${msg.kind} py-1 px-2 mt-2 mb-0`} style={styles.small} role="status">{msg.text}</div>}
        <label className="form-check mt-2 mb-0" style={styles.small}>
          <input type="checkbox" className="form-check-input" checked={Boolean(relay.auto_off)} disabled={busy} onChange={toggleAuto} />
          <span className="form-check-label">Auto-off after hours</span>
        </label>
        <div className="text-muted" style={styles.small}>{autoOffNote(relay)}</div>
        {activity.length > 0 && (
          <div className="mt-2" style={{ display: 'grid', gap: 2 }}>
            {activity.map((a) => (
              <div key={a.key} style={styles.activity(a.tone)}><span>{a.text}</span><span>{a.when}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShopLightsSection({ onRefused, paused }) {
  const { data, readFailed, load } = useShopLights({ active: !paused, onRefused });
  const relays = (data && Array.isArray(data.relays) && data.relays) || [];
  if (relays.length === 0) {
    if (!readFailed) return null;
    return <div className="alert alert-warning py-2 mb-4">Shop lights: JobQ isn't answering, so they can't be shown right now.</div>;
  }
  return (
    <>
      <h4 className="mb-2">Shop lights</h4>
      <div style={styles.grid} className="mb-4">
        {relays.map((r) => (
          <LightCard key={r.relay_id} relay={r} data={data} readFailed={readFailed} onChanged={load} onRefused={onRefused} />
        ))}
      </div>
    </>
  );
}
